// src/components/page-content-v4/modules/FieldRenderer.tsx
import React, { useState, useEffect } from 'react';
import EditorV3 from '@/components/editor-v1/EditorV3';
import 'react-quill/dist/quill.snow.css';
import { AlertCircle, Calendar, Clock, Image, FileText, List, Check, Plus, Trash2, ChevronDown, ChevronRight, X, ZoomIn, Maximize2, Minimize2 } from 'lucide-react';
import { Field, Asset } from '@/components/page-content-v4/types';
import FilePicker from '@/components/asset-manager/FilePicker';

interface FieldRendererProps {
  field: Field;
  value: any;
  onChange: (key: string, value: any) => void;
  onValidate?: (fieldKey: string, value: any) => string | null;
  error?: string | null;
  touched?: boolean;
}

// Image Preview Modal Component
const ImagePreviewModal: React.FC<{ 
  src: string; 
  alt: string; 
  onClose: () => void 
}> = ({ src, alt, onClose }) => {
  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Image preview"
    >
      <div className="relative max-w-[90vw] max-h-[90vh]">
        <button
          onClick={onClose}
          className="absolute -top-10 right-0 p-2 text-white/80 hover:text-white transition-colors"
          aria-label="Close preview (Escape)"
        >
          <X size={24} />
        </button>
        <img 
          src={src} 
          alt={alt} 
          className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        />
        <p className="mt-2 text-center text-white/70 text-sm truncate max-w-md mx-auto">
          {src.split('/').pop() || 'Image'}
        </p>
      </div>
    </div>
  );
};

// Constants for common styles
const STYLES = {
  baseInput: `w-full p-4 border border-gray-300 rounded-md text-sm transition duration-150 ease-in-out`,
  error: 'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500',
  normal: 'border-gray-300',
  iconContainer: 'pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3',
} as const;

// Input field type configurations
const INPUT_TYPES = {
  text: { icon: null },
  email: { icon: null },
  url: { icon: null },
  phone: { icon: null },
  number: { icon: null },
  date: { icon: Calendar },
  time: { icon: Clock },
  datetime: { icon: Calendar },
} as const;

const FieldRenderer: React.FC<FieldRendererProps> = ({ field, value, onChange, onValidate, error, touched }) => {
  const [isPickerOpen, setIsPickerOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState<string>('');
  const [collapsedItems, setCollapsedItems] = useState<Record<number, boolean>>({});
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const toggleItemCollapse = (index: number) => {
    setCollapsedItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };
  const validate = (val: any): string | null => {
    if (field.settings.required && (!val || (Array.isArray(val) && val.length === 0) || val === '')) {
      return 'This field is required';
    }

    if (field.type === 'url') {
      // eslint-disable-next-line
      const urlPattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
      if (Array.isArray(val)) {
        return val.some(url => !urlPattern.test(url)) ? 'Invalid URL format' : null;
      }
      return !urlPattern.test(val) ? 'Invalid URL format' : null;
    }

    return null;
  };

  const handleChange = (key: string, val: any) => {
    onChange(key, val);
    if (onValidate) {
      const validationError = validate(val);
      onValidate(key, validationError);
    }
  };

  const getInputClassName = (baseClass: string = STYLES.baseInput) =>
    `${baseClass} ${error ? STYLES.error : STYLES.normal}`;

  const renderInputIcon = (type: keyof typeof INPUT_TYPES) => {
    const Icon = INPUT_TYPES[type]?.icon;
    return Icon ? (
      <div className={STYLES.iconContainer}>
        <Icon className="h-5 w-5 text-gray-400" />
      </div>
    ) : null;
  };

  const renderBasicInput = () => {
    const type = field.type as keyof typeof INPUT_TYPES;
    const hasIcon = !!INPUT_TYPES[type]?.icon;

    return (
      <div className="relative">
        <input
          type={type === 'datetime' ? 'datetime-local' : type}
          id={field.key}
          name={field.key}
          value={value || ''}
          onChange={(e) => handleChange(field.key, e.target.value)}
          placeholder={field?.settings?.placeholder}
          onBlur={() => {
            if (onValidate) {
              const validationError = validate(value);
              onValidate(field.key, validationError);
            }
          }}
          required={field.settings.required}
          className={`${getInputClassName()} ${hasIcon ? 'pl-10' : 'px-3'} py-2 h-11`}
        />
        {renderInputIcon(type)}
        {touched && !error && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <Check className="h-5 w-5 text-green-500" />
          </div>
        )}
      </div>
    );
  };

  const renderUrlInputs = () => (
    <div className="space-y-6">
      {Array.isArray(value) ? (
        value.map((url, index) => (
          <div key={index} className="flex items-center gap-4">
            <input
              type="url"
              value={url}
              onChange={(e) => handleUrlChange(index, e.target.value)}
              onBlur={() => {
                if (onValidate) {
                  const validationError = validate(value);
                  onValidate(field.key, validationError);
                }
              }}
              placeholder={`Enter ${field.type} URL`}
              className={getInputClassName() + ' px-3 py-2 h-11'}
            />
            <button
              type="button"
              onClick={() => handleRemoveUrl(index)}
              className="text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-200 h-11 rounded-md w-11 flex items-center justify-center"
            >
              <Trash2 size={18} />
            </button>
          </div>
        ))
      ) : (
        <div className="relative">
          <input
            type="url"
            value={value || ''}
            onChange={(e) => handleChange(field.key, e.target.value)}
            onBlur={() => {
              if (onValidate) {
                const validationError = validate(value);
                onValidate(field.key, validationError);
              }
            }}
            placeholder={`Enter ${field.type} URL`}
            className={getInputClassName() + ' pl-10 pr-3 py-2 h-11'}
          />
          <div className={STYLES.iconContainer}>
            {field.type === 'image' ? <Image className="h-5 w-5 text-gray-400" /> : <FileText className="h-5 w-5 text-gray-400" />}
          </div>
        </div>
      )}
      {field.settings.isMultiSelect && (
        <div className='w-full inline-flex justify-end'>
        <button
          type="button"
          onClick={() => handleChange(field.key, [...(Array.isArray(value) ? value : []), ''])}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-primary-600 
             transition duration-150 ease-in-out"
        >
          <Plus size={18} className="mr-2" /> Add URL
        </button>
        </div>
      )}
    </div>
  );

  const handleAssetSelect = (files: Asset[]) => {
    const assetUrls = files.map((file) => file.originalFile);
    const newValue = field.settings.isMultiSelect
      ? [...(Array.isArray(value) ? value : []), ...assetUrls]
      : assetUrls[0] || '';
    handleChange(field.key, newValue);
    setIsPickerOpen(false);
  };

  const handleUrlChange = (index: number, newUrl: string) => {
    const newUrls = Array.isArray(value)
      ? value.map((url, i) => (i === index ? newUrl : url))
      : newUrl;
    handleChange(field.key, newUrls);
  };

  const handleRemoveUrl = (index: number) => {
    const newUrls = Array.isArray(value)
      ? value.filter((_, i) => i !== index)
      : '';
    handleChange(field.key, newUrls);
  };

  const renderNestedFieldsContent = () => {
    if (field.settings.isMulti) {
      const templates = Array.isArray(value) ? value : [];

      const handleExpandAllSub = () => {
        const newState: Record<number, boolean> = {};
        templates.forEach((_, i) => { newState[i] = false; }); // false = expanded
        setCollapsedItems(newState);
      };

      const handleCollapseAllSub = () => {
        const newState: Record<number, boolean> = {};
        templates.forEach((_, i) => { newState[i] = true; }); // true = collapsed
        setCollapsedItems(newState);
      };

      return (
        <div className="space-y-2">
          {/* Subsection Expand/Collapse Controls */}
          {templates.length > 1 && (
            <div className="flex items-center justify-end gap-2 mb-3">
              <button
                type="button"
                onClick={handleExpandAllSub}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 bg-white 
                  border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 
                  transition-colors duration-150 shadow-sm"
              >
                <Maximize2 size={13} />
                Expand
              </button>
              <button
                type="button"
                onClick={handleCollapseAllSub}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 bg-white 
                  border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 
                  transition-colors duration-150 shadow-sm"
              >
                <Minimize2 size={13} />
                Collapse
              </button>
            </div>
          )}
          
          {templates.map((templateValue, index) => {
            // Default to collapsed (true) for subsections
            const isCollapsed = collapsedItems[index] ?? true;
            
            return (
              <div key={index} className="relative bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm">
                <div 
                  className="flex items-center justify-between px-3 py-2.5 bg-gray-50/50 border-b border-gray-50 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => toggleItemCollapse(index)}
                >
                  <div className="flex items-center gap-2">
                    <div className={`transition-transform duration-200 ${isCollapsed ? '' : 'rotate-90'}`}>
                      <ChevronRight size={14} className="text-gray-500" />
                    </div>
                    <span className="text-sm font-medium text-gray-600">
                      {field.label} #{index + 1}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      const newTemplates = templates.filter((_, i) => i !== index);
                      onChange(field.key, newTemplates);
                    }}
                    className="text-gray-400 hover:text-red-500 hover:bg-red-50 h-7 w-7 rounded-md flex items-center justify-center transition-colors"
                    title="Remove item"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                {!isCollapsed && (
                  <div className="p-4 space-y-4">
                    {(field.templateFields || field.children || []).map((subField) => (
                      <FieldRenderer
                        key={`${subField.id}-${index}`}
                        field={subField}
                        value={templateValue[subField.key]}
                        onChange={(subKey, subValue) => {
                          const newTemplates = [...templates];
                          newTemplates[index] = {
                            ...newTemplates[index],
                            [subKey]: subValue,
                          };
                          onChange(field.key, newTemplates);
                        }}
                        onValidate={onValidate}
                        error={null}
                        touched={false}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          <button
            type="button"
            onClick={() => {
              const newTemplate: Record<string, any> = {};
              (field.templateFields || field.children || []).forEach((subField) => {
                newTemplate[subField.key] = null;
              });
              onChange(field.key, [...templates, newTemplate]);
            }}
            className="w-full flex items-center justify-center px-4 py-3 text-sm font-medium text-gray-600 
              bg-gray-50 hover:bg-gray-100 border-2 border-dashed border-gray-300 hover:border-gray-400 
              rounded-lg transition-colors"
          >
            <Plus size={18} className="mr-2" />
            Add {field.label}
          </button>
        </div>
      );
    }

    // Regular single template/container rendering - just render children directly
    return (
      <div className="space-y-4">
        {(field.templateFields || field.children || []).map((subField) => (
          <FieldRenderer
            key={subField.id}
            field={subField}
            value={(value || {})[subField.key]}
            onChange={(subKey, subValue) =>
              onChange(field.key, { ...value, [subKey]: subValue })
            }
            onValidate={onValidate}
            error={null}
            touched={false}
          />
        ))}
      </div>
    );
  };

  const renderFieldContent = () => {
    switch (field.type) {
      case 'text':
      case 'email':
      case 'url':
      case 'phone':
      case 'number':
      case 'date':
      case 'time':
      case 'datetime':
        return renderBasicInput();

      case 'textarea':
        return (
          <textarea
            id={field.key}
            name={field.key}
            value={value || ''}
            onChange={(e) => handleChange(field.key, e.target.value)}
            onBlur={() => {
              if (onValidate) {
                const validationError = validate(value);
                onValidate(field.key, validationError);
              }
            }}
            required={field.settings.required}
            rows={4}
            className={`${getInputClassName()} px-3 py-2`}
          />
        );

      case 'rich-text':
        return (
          <EditorV3
            initialContent={value || ''}
            onContentChange={(content) => handleChange(field.key, content)}
          />
          //     if (onValidate) {
          //       const validationError = validate(value);
          //       onValidate(field.key, validationError);
          //     }
          //   }}
          //   bounds={`#${field.key}`}
          //   className="h-[500px] overflow-auto"
          // />
        );

      case 'select':
      case 'radio':
      case 'checkbox':
        const options = Array.isArray(field.settings.options)
          ? field.settings.options
          : field.settings.options?.split(',').map((option: string) => option.trim());

        return field.type === 'select' ? (
          <div className="relative mt-1">
            <select
              id={field.key}
              name={field.key}
              value={value || ''}
              onChange={(e) => handleChange(field.key, e.target.value)}
              onBlur={() => {
                if (onValidate) {
                  const validationError = validate(value);
                  onValidate(field.key, validationError);
                }
              }}
              required={field.settings.required}
              className={getInputClassName() + ' appearance-none py-2 px-10 h-11'}
            >
              <option value="">Select an option</option>
              {options?.map((option: string, index: number) => (
                <option key={`${field.id}-option-${index}`} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <div className={STYLES.iconContainer}>
              <List className="h-5 w-5 text-gray-400" />
            </div>
            <ChevronDown className="w-5 h-5 absolute top-3.5 right-3" aria-hidden="true" />
          </div>
        ) : (
          <div className="mt-1 space-y-2">
            {options?.map((option: string, index: number) => (
              <div key={`${field.id}-option-${index}`} className="flex items-center">
                <input
                  type={field.type}
                  id={`${field.key}-${option}`}
                  name={field.key}
                  value={option}
                  checked={
                    field.type === 'checkbox'
                      ? (value || []).includes(option)
                      : value === option
                  }
                  onChange={(e) => {
                    const newValue = field.type === 'checkbox'
                      ? e.target.checked
                        ? [...(value || []), option]
                        : (value || []).filter((v: string) => v !== option)
                      : e.target.value;
                    handleChange(field.key, newValue);
                  }}
                  onBlur={() => {
                    if (onValidate) {
                      const validationError = validate(value);
                      onValidate(field.key, validationError);
                    }
                  }}
                  required={field.settings.required}
                  className={`focus:ring-primary-500 h-4 w-4 text-primary-600 border-neutral-300 ${
                    field.type === 'checkbox' ? 'rounded' : ''
                  }`}
                />
                <label htmlFor={`${field.key}-${option}`} className="ml-2 block text-sm text-neutral-700">
                  {option}
                </label>
              </div>
            ))}
          </div>
        );

      case 'image':
      case 'file':
        return field.settings.source === 'url' ? renderUrlInputs() : (
          <div className="space-y-2">
            {/* Image Preview Modal */}
            {previewImage && (
              <ImagePreviewModal 
                src={previewImage} 
                alt="Image preview" 
                onClose={() => setPreviewImage(null)} 
              />
            )}
            
            {/* Selected Assets - Compact List View */}
            {(Array.isArray(value) ? value : [value]).filter(Boolean).length > 0 && (
              <div className="space-y-2">
                {(Array.isArray(value) ? value : [value]).filter(Boolean).map((assetUrl, index) => (
                  <div key={index} className="flex items-center gap-3 p-2 bg-gray-50 border border-gray-200 rounded-lg group hover:border-gray-300 transition-colors">
                    {/* Thumbnail - Clickable for images */}
                    {field.type === 'image' ? (
                      <button
                        type="button"
                        onClick={() => setPreviewImage(assetUrl)}
                        className="flex-shrink-0 w-12 h-12 rounded-md bg-gray-100 border border-gray-200 overflow-hidden 
                          relative group/thumb cursor-pointer hover:ring-2 hover:ring-primary-500 hover:ring-offset-1 transition-all"
                        title="Click to preview"
                      >
                        <img 
                          src={assetUrl} 
                          alt={`Selected ${field.type}`} 
                          className="w-full h-full object-cover" 
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover/thumb:bg-black/30 flex items-center justify-center transition-colors">
                          <ZoomIn size={16} className="text-white opacity-0 group-hover/thumb:opacity-100 transition-opacity" />
                        </div>
                      </button>
                    ) : (
                      <div className="flex-shrink-0 w-12 h-12 rounded-md bg-gray-100 border border-gray-200 flex items-center justify-center">
                        <FileText size={20} className="text-gray-400" />
                      </div>
                    )}
                    
                    {/* Filename */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-700 truncate" title={assetUrl}>
                        {assetUrl.split('/').pop() || 'Unnamed file'}
                      </p>
                      <p className="text-xs text-gray-400 truncate">{field.type === 'image' ? 'Image' : 'File'}</p>
                    </div>
                    
                    {/* Remove Button */}
                    <button
                      type="button"
                      onClick={() => handleRemoveUrl(index)}
                      className="flex-shrink-0 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                      aria-label="Remove item"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Asset Picker Button - Secondary style when assets exist */}
            <button
              type="button"
              onClick={() => setIsPickerOpen(true)}
              className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors
                ${(Array.isArray(value) ? value : [value]).filter(Boolean).length > 0 
                  ? 'text-gray-600 bg-white border border-gray-300 hover:bg-gray-50 hover:border-gray-400' 
                  : 'text-white bg-primary-500 hover:bg-primary-600 shadow-sm'
                }`}
            >
              <Image size={16} className="mr-2" /> 
              {(Array.isArray(value) ? value : [value]).filter(Boolean).length > 0 
                ? `Add ${field.type === 'image' ? 'Image' : 'File'}` 
                : `Choose ${field.type === 'image' ? 'Image' : 'File'}`}
            </button>
            {isPickerOpen && (
              <FilePicker
                allowedFileTypes={field.settings.allowedTypes?.split(',').map((type: string) => type.trim()) || []}
                multiSelect={field.settings.isMultiSelect || false}
                onSelect={handleAssetSelect}
                onClose={() => setIsPickerOpen(false)}
              />
            )}
          </div>
        );

      case 'container':
      case 'custom_template': 
        return renderNestedFieldsContent();

      case 'multiselect':
        return (
          <div className="relative">
            <div className={`flex flex-wrap items-center gap-1 p-2 ${getInputClassName()}`}>
              {Array.isArray(value) && value.map((tag, index) => (
                <span 
                  key={index} 
                  className="inline-flex items-center bg-blue-100 text-blue-800 text-sm px-2 py-0.5 rounded-md"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      handleRemoveTag(index);
                    }}
                    className="ml-1 text-blue-600 hover:text-blue-800"
                  >
                    ×
                  </button>
                </span>
              ))}
              <input
                type="text"
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder={Array.isArray(value) && value.length > 0 ? '' : `Type and press Enter or comma to add ${field.label}...`}
                className="flex-1 outline-none border-0 focus:ring-0 px-4 py-2 min-w-[120px] bg-transparent"
              />
            </div>
          </div>
        );

      default:
        return <p className="mt-1 text-sm text-red-600">Unsupported field type: {field.type}</p>;
    }
  };

  // New function to handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  // New function to handle key down events
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const trimmedValue = inputValue.trim();
      
      // Ensure value is an array before checking includes
      const currentValues = Array.isArray(value) ? value : [];
      
      if (trimmedValue && !currentValues.includes(trimmedValue)) {
        handleChange(field.key, [...currentValues, trimmedValue]);
        setInputValue(''); // Clear input after adding
      }
    }
  };

  // New function to handle tag removal
  const handleRemoveTag = (index: number) => {
    const newTags = Array.isArray(value) ? value.filter((_, i) => i !== index) : [];
    handleChange(field.key, newTags);
  };

  // Hide label for container/custom_template as they're already shown in CollapsibleSection header
  const isContainerType = field.type === 'container' || field.type === 'custom_template';

  return (
    <div className="space-y-2">
      {!isContainerType && (
        <label htmlFor={field.key} className="block text-sm font-medium text-gray-700">
          {field.label}
          {field.settings.required && <span className="text-red-500 ml-1">*</span>}
          {field.settings.placeholder && (
            <span className="ml-2 text-xs text-gray-500 font-normal">
              {field.settings.placeholder}
            </span>
          )}
        </label>
      )}
      {renderFieldContent()}
      {touched && error && (
        <p className="mt-2 text-sm text-red-600 flex items-center">
          <AlertCircle className="h-4 w-4 mr-1" />
          {error}
        </p>
      )}
    </div>
  );
};

export default React.memo(FieldRenderer);
